import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import moment, { Moment } from 'moment';
import useQuery from 'utils/hooks/useQuery';
import * as util from 'utils/dashboard'; // skipcq: JS-C1003
import * as utils from 'utils/summary'; // skipcq: JS-C1003
import { SUMMARY } from 'state/summary/type';
import { PAYMENTS } from 'state/payment/type';
import usePayment from 'state/payment/hooks/usePayment';
import { TIPS_TABS } from 'utils/constants';

interface UseTipsSummaryArgs {
  activeTab: string;
  summaryType: string;
  intervalTypes: SUMMARY.IIntervalType[];
  toSetIntervalTypes: React.Dispatch<
    React.SetStateAction<SUMMARY.IIntervalType[]>
  >;
  excludeHourly: boolean;
}

interface UseTipsSummaryReturn {
  dateFromValue: Moment;
  dateToValue: Moment;
  comparisonDate: [string, string];
  activeFreq: SUMMARY.IIntervalType;
  setActiveFreq: React.Dispatch<React.SetStateAction<SUMMARY.IIntervalType>>;
  products: PAYMENTS.TipByPaymentTypeAggregateItem[];
  searchString: string | undefined;
}

const useTipsSummary = ({
  activeTab,
  summaryType,
  intervalTypes,
  toSetIntervalTypes,
  excludeHourly,
}: UseTipsSummaryArgs): UseTipsSummaryReturn => {
  const location = useLocation();
  const query = useQuery(location?.search);
  const { payment, toFetchTipsSummary } = usePayment();

  const fromDate = moment().subtract(7, 'day').startOf('day');
  const todayDate = moment().endOf('day');

  const [dateFromValue, setDateFromValue] = useState<Moment>(fromDate);
  const [dateToValue, setDateToValue] = useState<Moment>(todayDate);
  const [activeFreq, setActiveFreq] = useState<SUMMARY.IIntervalType>(
    SUMMARY.IIntervalType.DAILY
  );

  const dates = query.get('dates')?.split(',') || [];
  const dateFrom = dates.length > 0 ? dates[0].replace(' ', '+') : '';
  const dateTo = dates.length > 1 ? dates[1].replace(' ', '+') : '';
  const searchString = query.get('search') || undefined;

  const comparisonDate: [string, string] = useMemo(() => {
    const fromISO = dateFromValue.toISOString();
    const toISO = dateToValue.toISOString();
    const calculated = util.getComparisonDateTime(fromISO, toISO);

    if (calculated && calculated[0] && calculated[1]) {
      return [calculated[0], calculated[1]];
    }

    const intervalDays = dateToValue.diff(dateFromValue, 'days');
    return [
      moment(fromISO)
        .subtract(intervalDays + 1, 'days')
        .toISOString(),
      moment(toISO)
        .subtract(intervalDays + 1, 'days')
        .toISOString(),
    ];
  }, [dateFromValue, dateToValue]);

  useEffect(() => {
    if (activeTab === TIPS_TABS[1].label) {
      setDateFromValue(fromDate);
      setDateToValue(todayDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (intervalTypes.length > 0 && activeTab === TIPS_TABS[0].label) {
      const actualStartDate = dateFrom || dateFromValue.toISOString();
      const actualEndDate = dateTo || dateToValue.toISOString();

      const freshComparisonDate = util.getComparisonDateTime(
        actualStartDate,
        actualEndDate
      );

      toFetchTipsSummary({
        start_date: actualStartDate,
        end_date: actualEndDate,
        summary_type: summaryType,
        interval_types: intervalTypes.join(),
        search_string: searchString,
        prev_start_date: freshComparisonDate?.[0],
        prev_end_date: freshComparisonDate?.[1],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, intervalTypes, activeTab, summaryType, searchString]);

  useEffect(() => {
    const interval = util.intervalInDays(
      dateFrom || dateFromValue.toISOString(),
      dateTo || dateToValue.toISOString()
    );
    if (excludeHourly) {
      toSetIntervalTypes(utils.mapIntervalToFrequencyBanking(interval));
    } else {
      toSetIntervalTypes(util.mapIntervalToFrequency(interval));
    }
    if (intervalTypes.length > 0) {
      setActiveFreq(intervalTypes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, dateFromValue, dateToValue]);

  const products = useMemo(() => {
    const indexed = payment?.tipsSummaryPaymentType[activeFreq] || [];
    return indexed.map((a, index) => ({
      ...a,
      key: `${a.date}-${activeFreq}-${index}`,
    }));
  }, [payment?.tipsSummaryPaymentType, activeFreq]);

  useEffect(() => {
    if (intervalTypes.length > 0) {
      setActiveFreq(intervalTypes[0]);
    }
  }, [intervalTypes]);

  return {
    dateFromValue,
    dateToValue,
    comparisonDate,
    activeFreq,
    setActiveFreq,
    products,
    searchString,
  };
};

export default useTipsSummary;
